import React, { useState, useEffect } from 'react';
import { Facebook, Instagram, Menu, MessageCircle, X } from 'lucide-react';
import { followInternalLink } from '../../lib/navigation';
import { brand, navLinks } from '../../mock';
import { openWhatsApp, WHATSAPP_NUMBER } from '../../lib/whatsapp';

const socialLinks = [
  { label: 'Facebook', href: 'https://www.facebook.com/p/Kutay-%C3%96zt%C3%BCrk-61561196368618/', Icon: Facebook },
  { label: 'Instagram', href: 'https://www.instagram.com/canakkalekonutrehberi/', Icon: Instagram },
  { label: 'WhatsApp', href: `https://wa.me/${WHATSAPP_NUMBER}`, Icon: MessageCircle },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const solid = scrolled || open;
  const txt = solid ? 'var(--ckr-text)' : 'rgba(246,242,232,0.92)';
  const brandColor = solid ? 'var(--ckr-petrol)' : '#f6f2e8';
  const subColor = solid ? 'var(--ckr-muted)' : 'rgba(246,242,232,0.6)';

  const handleNav = (e, href) => {
    setOpen(false);
    followInternalLink(e, href);
  };

  return (
    <header className="ckr-navbar" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
      transition: 'background-color .3s ease, box-shadow .3s ease, border-color .3s ease',
      backgroundColor: solid ? 'rgba(246,243,236,0.9)' : 'transparent',
      backdropFilter: solid ? 'blur(12px)' : 'none',
      WebkitBackdropFilter: solid ? 'blur(12px)' : 'none',
      borderBottom: solid ? '1px solid var(--ckr-border)' : '1px solid transparent',
    }}>
      <div className="ckr-site ckr-navbar-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Brand lockup */}
        <a className="ckr-navbar-brand" href="/" onClick={(e) => handleNav(e, '/')} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', minWidth: 0 }}>
          <span className="ckr-navbar-logo" style={{
            flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 10,
            background: solid ? 'var(--ckr-petrol)' : 'rgba(246,242,232,0.12)',
            border: solid ? 'none' : '1px solid rgba(246,242,232,0.35)',
            color: solid ? '#f3efe4' : '#f6f2e8', fontFamily: 'Fraunces, serif', fontWeight: 600, fontSize: 16, letterSpacing: '0.02em',
            transition: 'background .3s ease, color .3s ease, border-color .3s ease',
          }}>ÇKR</span>
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
            <span className="ckr-navbar-title" style={{ fontFamily: 'Fraunces, serif', fontWeight: 600, color: brandColor, transition: 'color .3s ease' }}>{brand.name}</span>
            <span style={{ fontSize: 11.5, color: subColor, letterSpacing: '0.06em', whiteSpace: 'nowrap', transition: 'color .3s ease' }}>{brand.person}</span>
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden xl:flex ckr-main-nav" aria-label="Ana menü" style={{ alignItems: 'center', gap: 8 }}>
          {navLinks.map((l) => (
            <a className="ckr-nav-button" key={l.href} href={l.href} onClick={(e) => handleNav(e, l.href)}
              style={{ whiteSpace: 'normal' }}
              >{l.label}</a>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden xl:flex ckr-nav-follow" style={{ alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: txt, whiteSpace: 'nowrap', opacity: 0.82 }}>Beni takip edin</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {socialLinks.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="ckr-nav-social"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>

        {/* Mobile toggle */}
        <button className="xl:hidden" onClick={() => setOpen(!open)} aria-label="Menü" aria-expanded={open} aria-controls="ckr-mobile-navigation"
          style={{ background: 'transparent', border: 'none', color: brandColor, cursor: 'pointer', padding: 6 }}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div id="ckr-mobile-navigation" className="xl:hidden" style={{ background: 'rgba(246,243,236,0.98)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--ckr-border)' }}>
          <div className="ckr-site" style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 8, paddingBottom: 18 }}>
            {navLinks.map((l) => (
              <a className="ckr-nav-button ckr-nav-button-mobile" key={l.href} href={l.href} onClick={(e) => handleNav(e, l.href)}
                >{l.label}</a>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '13px 4px 0' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ckr-muted)' }}>Beni takip edin</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {socialLinks.map(({ label, href, Icon }) => (
                  <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} className="ckr-nav-social">
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>
            <button className="ckr-btn ckr-btn-primary" style={{ marginTop: 14 }} onClick={() => openWhatsApp('Merhaba, Çanakkale Konut Rehberi üzerinden bilgi almak istiyorum.')}>
              <MessageCircle size={16} /> WhatsApp ile iletişim
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
